/**
 * objectionIdentity.ts
 *
 * Single source of truth for objection identification, labeling,
 * and categorization. Used by both Prep Mode (DecisionTreeRebuttals)
 * and Call Mode (CallModeObjections) to ensure identical display.
 *
 * Place at: src/utils/objectionIdentity.ts
 */

import type { ObjectionRebuttal } from "../types/caseView";

// ============================================
// ENRICHED TYPE
// ============================================

export interface EnrichedObjection extends ObjectionRebuttal {
  /** Stable identifier derived from objection content */
  stableId: string;
  /** Canonical short label for chip display */
  shortLabel: string;
  /** Category for grouping */
  category: string;
  /** Original array position from parseAIPrep (canonical order) */
  sourceIndex: number;
}

// ============================================
// CATEGORIES
// ============================================

export interface ObjectionCategory {
  id: string;
  label: string;
  keywords: string[];
}

export const OBJECTION_CATEGORIES: ObjectionCategory[] = [
  { id: "step_therapy", label: "Step Therapy", keywords: ["step therapy", "insufficient trial", "trial duration", "try first", "not tried", "nsaid", "conservative", "exhaust"] },
  { id: "medical_necessity", label: "Medical Necessity", keywords: ["not medically necessary", "medical necessity", "not indicated", "not warranted"] },
  { id: "alternative", label: "Alternative Treatment", keywords: ["alternative", "could retry", "ppi", "co-therapy", "protection", "switch", "substitute"] },
  { id: "documentation", label: "Documentation", keywords: ["documentation", "insufficient", "records", "need specific", "chart notes", "evidence"] },
  { id: "imaging", label: "Imaging Sequencing", keywords: ["x-ray", "ct", "plain film", "mri", "imaging", "scan"] },
  { id: "conservative", label: "Conservative Care", keywords: ["conservative", "physical therapy", "pt", "therapy", "treatment"] },
  { id: "redflags", label: "Red Flags / Severity", keywords: ["red flag", "emergency", "urgent", "neurologic", "deficit", "progressive", "severity"] },
  { id: "diagnosis", label: "Diagnosis Framing", keywords: ["chronic", "routine", "non-specific", "degenerative", "normal", "age", "mechanical"] },
  { id: "other", label: "Other Objections", keywords: [] },
];

// ============================================
// CANONICAL SHORT LABELS
// ============================================

const OBJECTION_SHORT_LABELS: Record<string, string> = {
  "conservative management hasn't been tried long enough": "Needs more conservative treatment",
  "conservative therapy not exhausted": "Conservative therapy not exhausted",
  "physical therapy not completed": "Physical therapy not completed",
  "needs more physical therapy": "Needs more physical therapy",
  "medication therapy not tried": "Hasn't tried medications first",
  "nsaids not tried": "NSAIDs haven't been tried",
  "plain films should be tried first": "Needs X-ray before MRI",
  "mri not indicated": "MRI not indicated",
  "no red flag symptoms documented": "No red flags documented",
  "no neurologic deficits": "No neurologic deficits",
  "not medically necessary": "Not medically necessary",
  "documentation insufficient": "Documentation is insufficient",
};

// ============================================
// FUNCTIONS
// ============================================

/**
 * Generate a stable ID from objection text.
 * Deterministic: same text always produces same ID.
 */
export function generateObjectionId(objection: string, index: number): string {
  const normalized = objection.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_");
  const prefix = normalized.slice(0, 32);
  return `obj_${prefix}_${index}`;
}

/**
 * Shorten an objection to a natural chip-friendly label.
 * Single source of truth — used by both Prep and Call Mode.
 */
export function shortenObjection(objection: string): string {
  const lower = objection.toLowerCase().trim();

  // Exact pattern matches
  for (const [pattern, label] of Object.entries(OBJECTION_SHORT_LABELS)) {
    if (lower.includes(pattern) || pattern.includes(lower.slice(0, 30))) {
      return label;
    }
  }

  // Heuristic matches
  if (lower.includes("insufficient trial") || lower.includes("trial duration")) {
    return "Trial duration insufficient";
  }
  if (lower.includes("ppi") || lower.includes("co-therapy") || lower.includes("retry")) {
    return "Should retry with GI protection";
  }
  if (lower.includes("documentation") || lower.includes("need specific")) {
    return "Needs more documentation";
  }
  if (lower.includes("conservative") && (lower.includes("long enough") || lower.includes("exhaust"))) {
    return "Needs more conservative treatment";
  }
  if (lower.includes("not medically necessary") || lower.includes("medical necessity")) {
    return "Not medically necessary";
  }

  // Fallback: clean up the original objection text
  // Strip leading articles/filler, capitalize, cap at ~6 words
  let fallback = objection
    .replace(/^(the|a|an|that|this|they say|reviewer says|because)\s+/i, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  const words = fallback.split(/\s+/);
  if (words.length > 7) {
    fallback = words.slice(0, 7).join(" ");
    // Don't end on a small word
    if (/^(the|a|an|to|of|in|is|for|and|or|not)$/i.test(words[6])) {
      fallback = words.slice(0, 6).join(" ");
    }
  }
  // Capitalize first letter
  return fallback.charAt(0).toUpperCase() + fallback.slice(1);
}

/**
 * Categorize an objection by content.
 */
export function categorizeObjection(objection: string): string {
  const lower = objection.toLowerCase();

  for (const cat of OBJECTION_CATEGORIES) {
    if (cat.id === "other") continue;
    if (cat.keywords.some((kw) => lower.includes(kw))) {
      return cat.id;
    }
  }

  return "other";
}

/**
 * Enrich raw ObjectionRebuttal[] with stable IDs, labels, and categories.
 * Call this ONCE in the parent component, then pass the enriched array
 * to both Prep Mode and Call Mode.
 *
 * The returned array preserves original order — this is the canonical order.
 */
export function enrichObjections(
  pairs: ObjectionRebuttal[]
): EnrichedObjection[] {
  // Cost objection keywords — filtered out before either mode sees them
  const COST_KEYWORDS = ["cost", "expensive", "generic", "formulary", "tier", "price", "cheaper"];

  return pairs
    .filter((pair) => {
      const lower = pair.objection.toLowerCase();
      return !COST_KEYWORDS.some((kw) => lower.includes(kw));
    })
    .map((pair, index) => ({
      ...pair,
      stableId: generateObjectionId(pair.objection, index),
      shortLabel: shortenObjection(pair.objection),
      category: categorizeObjection(pair.objection),
      sourceIndex: index,
    }));
}